import * as React from "react";
import { cn } from "./cn";

/**
 * Skeleton — a grey stand-in shaped like the thing that is coming, for waits
 * where the layout is already known (a list of classes, a card, a table row).
 * It keeps the page from jumping about once the real content lands.
 *
 * Size it with the usual width/height classes: `<Skeleton className="h-4 w-40" />`.
 * Build a placeholder out of several, shaped like the real thing — a title bar,
 * a couple of text lines of different lengths, a block where the picture goes.
 *
 * A band of light sweeps across it so a wait always looks alive. The sweep is a
 * pseudo-element, so the box needs no extra markup. For anyone who has asked
 * for reduced motion the site-wide rule in theme.css stills it, which parks the
 * band off the right-hand edge and leaves the plain grey behind — the component
 * carries no second switch of its own.
 *
 * Skeletons are decoration — a screen reader should hear the one status message
 * on the region that is loading, not a dozen empty boxes — so each is hidden
 * from assistive tech. Wrap a group in something carrying `role="status"` and a
 * plain label such as "Loading classes".
 */
export type SkeletonProps = React.HTMLAttributes<HTMLDivElement>;

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn(
        "relative isolate overflow-hidden rounded bg-ink/10",
        "after:absolute after:inset-0 after:content-[''] after:bg-gradient-to-r after:from-transparent after:via-ink/25 after:to-transparent",
        "after:animate-shimmer",
        className,
      )}
      {...props}
    />
  ),
);
Skeleton.displayName = "Skeleton";
